import findIndex from "lodash/findIndex";
import isArray from "lodash/isArray";
import isString from "lodash/isString";
import isNumber from "lodash/isNumber";
import map from "lodash/map";
import omit from "lodash/omit";

import {
  INIT_TABLE_FIELDS, CHANGE_TABLE_FIELDS, UPDATE_POSITION_TABLE_FIELDS,
  CHANGE_WIDTH_TABLE_FIELD, CHANGE_SORT_TABLE, CHANGE_PAGE,
  FETCH_TABLE_PENDING, FETCH_TABLE_SUCCESS, FETCH_TABLE_ERROR,
  ADD_TABLE_DATA, PUT_TABLE_DATA, RESET_TABLE_DATA, REMOVE_TABLE_DATA_BY_ID,
  REPLACE_TABLE_DATA_BY_ID, REMOVE_TABLE
} from "./constants";


const initialState = {};

// Delete and Replace objects findConfig CASES:
//   1) 24 (string or number) - delete object with id === 24
//   2) [24, 36] (arr[string] or arr[number]) - delete objects with id === 24 and id === 36
//   3) {'meta': 24} (arr[string] || arr[number]) - delete objects with field meta === 24
//   4) {'meta.project': [24, 36], 'meta.dirty': true} ({ string: arr[string] || arr[number] }) - delete objects with meta[project] === (24 and 36) and meta[dirty] === true

const findRowData = (row, findConfig, replaceValue) => {
  const isTrueCondition = isString(findConfig) || isNumber(findConfig)
    ? row.id === findConfig
    : isArray(findConfig)
      ? findConfig.filter(valueForDelete => row.id === valueForDelete).length
      : map(findConfig, (value, key) => isArray(value)
        ? value.filter(valueForDelete =>
            key.split('.').reduce((acc, next) => acc[next], row) === valueForDelete).length
        : row.id === findConfig.id
          ? key.split('.').reduce((acc, next) => acc[next], row) === value
          : false
      ).filter(a => a).length;

  if (!replaceValue) return isTrueCondition;
  return isTrueCondition ? replaceValue : row;
};


export default (state = initialState, action) => {
  switch (action.type) {
    case INIT_TABLE_FIELDS:
      const initTableFields = action.payload.keys.relativeValue
        ? {
            ...state[action.payload.name],
            [action.payload.keys.relativeValue]: {
                ...(state[action.payload.name] && state[action.payload.name][action.payload.keys.relativeValue]),
                ...omit(action.payload.keys, ['relativeValue']),
                error: null
            }
          }
        : {
              ...state[action.payload.name], ...action.payload.keys,
              error: null
          };

      return { ...state, [action.payload.name]: initTableFields};

    case REMOVE_TABLE:
      const removedTable = {...state};
      if (removedTable[action.payload.name]) delete removedTable[action.payload.name];
      return removedTable;

    case CHANGE_TABLE_FIELDS:
      const { id, tableName } = action.payload;
      const filterPosition = findIndex(state[tableName].fields, item => item.field === id);
      const newCheckedColumnsKeys = [...state[tableName].fields].map((fieldConfig, index) => ({
          ...fieldConfig,
          checked: index === filterPosition ? !fieldConfig.checked : fieldConfig.checked
      }));

      return { ...state, [tableName]: {
        ...state[tableName], fields: newCheckedColumnsKeys
      }};

    case UPDATE_POSITION_TABLE_FIELDS:
      const { updatedFields } = action.payload;

      return { ...state, [action.payload.tableName]: {
        ...state[action.payload.tableName], fields: updatedFields
      }};

    case CHANGE_WIDTH_TABLE_FIELD:
      const { column, width } = action.payload;

      let newFields = action.payload.relativeValue
        ? [...state[action.payload.tableName][action.payload.relativeValue].fields]
        : [...state[action.payload.tableName].fields];
      const fieldIndex = typeof column === 'number' ? column : findIndex(newFields, column);

      newFields.splice(fieldIndex, 1, {...newFields[fieldIndex], width});

      const changeWidthTable = action.payload.relativeValue
          ? {
            ...state[action.payload.tableName],
            [action.payload.relativeValue]: {
              ...state[action.payload.tableName][action.payload.relativeValue],
              fields: newFields
            }
          }
          : { ...state[action.payload.tableName], fields: newFields };

      return { ...state, [action.payload.tableName]: changeWidthTable };

    case CHANGE_SORT_TABLE:
      const changeSortTable = action.payload.relativeValue
        ? {
          ...state[action.payload.tableName],
          [action.payload.relativeValue]: {
            ...state[action.payload.tableName][action.payload.relativeValue],
            sort: action.payload.sort,
            loading: action.payload.loading
          }
        }
        : { ...state[action.payload.tableName], sort: action.payload.sort, loading: action.payload.loading };

      return { ...state, [action.payload.tableName]: changeSortTable};

    case CHANGE_PAGE:
      return { ...state, [action.payload.tableName]: {
        ...state[action.payload.tableName],
        offset: action.payload.offset,
        page: action.payload.page
      }};

    case FETCH_TABLE_PENDING:
      const fetchTablePending = action.externalData.relativeValue
        ? {
            ...state[action.externalData.name],
            [action.externalData.relativeValue]: {
              ...state[action.externalData.name][action.externalData.relativeValue],
              loading: true, error: null
            }
          }
        : { ...state[action.externalData.name], loading: true, error: null };

      return { ...state, [action.externalData.name]: fetchTablePending};


    case FETCH_TABLE_SUCCESS:
      const fetchTableSuccess = action.externalData.relativeValue
        ? {
            ...state[action.externalData.name],
            [action.externalData.relativeValue]: {
                ...state[action.externalData.name][action.externalData.relativeValue],
                loading: false,
                ...action.payload
            }
        }
        : { ...state[action.externalData.name], loading: false, ...action.payload };

      return { ...state, [action.externalData.name]: fetchTableSuccess};

    case FETCH_TABLE_ERROR:
      const fetchTableError = action.externalData.isNested
          ? {
              ...state[action.externalData.name],
              [action.externalData.relativeValue]: {
                  ...state[action.externalData.name][action.externalData.relativeValue],
                  loading: false,
                  error: action.error
              }
          }
          : { ...state[action.externalData.name], loading: false, error: action.error };

      return { ...state, [action.externalData.name]: fetchTableError};

    case ADD_TABLE_DATA:
      const addData = [
        ...(state[action.payload.tableName].data
          ? state[action.payload.tableName].data
          : []),
        ...(action.payload.data
          ? isArray(action.payload.data)
            ? action.payload.data
            : [action.payload.data]
          : [])
      ];

      return { ...state, [action.payload.tableName]: {
        ...state[action.payload.tableName], data: addData, count: addData.length
      }};

    case PUT_TABLE_DATA:
      const putData = [
        ...(action.payload.data
          ? isArray(action.payload.data)
            ? action.payload.data
            : [action.payload.data]
          : [])
      ];

      return { ...state, [action.payload.tableName]: {
        ...state[action.payload.tableName], data: putData, count: putData.length
      }};

    case RESET_TABLE_DATA:
      const resetTable = {...state[action.payload.tableName]};
      if (!action.payload.nested) {
        delete resetTable.data;
        delete resetTable.count;
      }
      return { ...state, [action.payload.tableName]: action.payload.nested ? {} : {...resetTable}};

    case REMOVE_TABLE_DATA_BY_ID:
      const tableInfo = {...state[action.payload.tableName]};

      if (tableInfo && tableInfo.data && action.payload.findConfig) {
        tableInfo.data = tableInfo.data.filter(row => !findRowData(row, action.payload.findConfig));
        tableInfo.count = tableInfo.data.length;
      } else {
        return state;
      }

      return { ...state, [action.payload.tableName]: { ...tableInfo }};

    case REPLACE_TABLE_DATA_BY_ID:
      const replaceTableInfo = {...state[action.payload.tableName]};

      if (replaceTableInfo && replaceTableInfo.data && action.payload.findConfig && action.payload.replaceValue) {
        replaceTableInfo.data = replaceTableInfo.data.map(row =>
          findRowData(row, action.payload.findConfig, action.payload.replaceValue));
      } else {
        return state;
      }

      return { ...state, [action.payload.tableName]: { ...replaceTableInfo }};

    default:
      return state;
  }
};
